import { Button } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { getUserProfile, getUserToken, patchUserProfile, putUserNotification } from '../../redux/actions/userActions.js';
import { useSelector } from 'react-redux';

import { action } from 'lib/actionHelpers';
import setQuery from 'lib/setQuery.js';
import useBoundForm from '../../hooks/useBoundForm.js';
import BoundFormGroup from '../../components/boundForm/BoundFormGroup.js';
import selectors from '../../selectors/selectors.js';
import { Toggle } from '../../components/Toggle.js';
import removeUndefinedFields from '../../lib/removeUndefinedFields.js';
import { formatPhoneNumber } from 'lib/numUtils.js';

const getInitialState = ({ profile, currentUserDestinationPhoneId }) => {
  return {
    first_name: (profile || {}).first_name,
    last_name: (profile || {}).last_name,
    user_phone: (profile || {}).user_phone,
    user_extension: (profile || {}).user_extension,
    title: (profile || {}).title,
    ...(currentUserDestinationPhoneId ? { destinationPhoneId: currentUserDestinationPhoneId } : {}),
  };
};

const EditProfileForm = () => {
  const { profile } = useSelector((state) => state.user);
  const currentUserId = useSelector(selectors.userId);
  const users = useSelector(selectors.serviceProviderUsers);
  const currentUserData = (users || []).find((n) => n.id === currentUserId);
  const notifications = useSelector(selectors.serviceProviderNotifications) || {};
  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const destinationPhoneNumbers = useSelector(selectors.destinationPhoneNumbers);
  const currentUserDestinationPhoneId = ((users || []).find((n) => n.id === (currentUserData || {}).id) || {}).userNotificationsDestinationPhoneId;

  const isTresioUser = (profile || {}).tresio_user === '1' ? true : false;

  const destinationPhoneOptions = (destinationPhoneNumbers || []).map((n) => ({
    value: n.id,
    destinationPhoneId: n.id,
    label: `${formatPhoneNumber(n.phoneNumber)} (${n.cookedLabel || n.label})`,
  }));
  const [fetchingProfile, setFetchingProfile] = useState(true);

  const form = useBoundForm({ initialState: getInitialState({ profile, currentUserDestinationPhoneId }) });

  const fields = !isTresioUser
    ? [
        [
          { id: 'first_name', placeholder: 'First Name' },
          { id: 'last_name', placeholder: 'Last Name' },
        ],
        [{ id: 'title', placeholder: 'Title' }],
        [
          { id: 'destinationPhoneId', placeholder: 'My Phone Number', options: destinationPhoneOptions, nullable: true, nullableLabel: 'None' },
          { id: 'user_phone', placeholder: 'User Phone (Manual Entry)', format: 'phone', disabled: form.state.destinationPhoneId === '0' || form.state.destinationPhoneId },
        ],
      ]
    : [
        [
          { id: 'first_name', placeholder: 'First Name' },
          { id: 'last_name', placeholder: 'Last Name' },
        ],
        [
          { id: 'title', placeholder: 'Title' },
          { id: 'user_phone', placeholder: 'User Phone (Manual Entry)', format: 'phone', disabled: form.state.destinationPhoneId === '0' || form.state.destinationPhoneId },
        ],
      ];

  useEffect(() => {
    getUserProfile(() => {
      setFetchingProfile(false);
    });
  }, []);

  useEffect(() => {
    if (currentUserDestinationPhoneId) {
      form.reset(getInitialState({ profile, currentUserDestinationPhoneId }));
    } else {
      form.reset(getInitialState({ profile }));
    }
  }, [profile, currentUserDestinationPhoneId]);

  useEffect(() => {
    if (currentUserDestinationPhoneId) {
      form.setState({ destinationPhoneId: currentUserDestinationPhoneId });
    }
  }, [currentUserDestinationPhoneId]);

  const handleCloseClick = () => {
    setQuery({ profile_view: null });
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    if (form.state.destinationPhoneId === null || form.state.destinationPhoneId) {
      putUserNotification({
        userId: currentUserData.id,
        serviceProviderId,
        destinationPhoneId: form.state.destinationPhoneId,
        ...(form.state.destinationPhoneId === null || (form.state.destinationPhoneId && currentUserData.id)
          ? {
              callback: () => {
                action('PATCH_DESTINATION_PHONE', { userId: currentUserData.id, destinationPhoneId: form.state.destinationPhoneId });
              },
            }
          : {}),
      });
    }
    setQuery({ profile_view: null });
    const payload = removeUndefinedFields({
      ...form.state,
      user_phone: (form.state.user_phone || '').replace(/\D/g, ''),
    });
    patchUserProfile(payload, () => {
      action('PATCH_USER_PROFILE_SUCCESS', { data: { ...form.state } });
      getUserToken({ serviceProviderId });
    });
  };

  const handleToggleClick = (n, type) => {
    const newValue = currentUserData[n] === '1' ? '0' : '1';
    const payload = {
      userId: currentUserData.id,
      serviceProviderId,
      [type]: newValue,
    };
    putUserNotification({
      ...payload,
      callback: () => {
        action('PATCH_USER_EMAIL_TEXT_NOTIFICATION_TOGGLE', { ...payload, type });
      },
    });
  };

  return (
    <>
      {!fetchingProfile && (
        <>
          <div className={'pane-header'}>
            <h1 className={'heading'}>Edit Profile</h1>
            <div className="subheading" />
          </div>
          <div className={'pane-content'}>
            <div className={'section-heading'}>Basic Information</div>
            <BoundFormGroup formId={form.id} fields={fields} />{' '}
            {!!notifications.id && !isTresioUser && (
              <>
                <div className={'section-heading'}>New Text Message Notifications</div>
                <div>
                  <div className={'toggle-row'}>
                    <div className={'label'}>Receive Email Notifications</div>
                    <Toggle active={(currentUserData || {}).userNotificationsEmail === '1'} onClick={() => handleToggleClick('userNotificationsEmail', 'email')} />
                  </div>
                  <div className={'toggle-row'}>
                    <div className={'label'}>Receive Text Notifications</div>
                    <Toggle active={(currentUserData || {}).userNotificationsText === '1'} onClick={() => handleToggleClick('userNotificationsText', 'text')} />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={'pane-footer'}>
            <div className="button-row">
              <Button variant="primary" type="submit" onClick={handleSaveClick}>
                Save
              </Button>
              <Button variant="secondary" onClick={handleCloseClick}>
                Close
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default EditProfileForm;
